<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import BotsAPI from 'dashboard/api/bots';
import PipelinesAPI from 'dashboard/api/pipelines';
import { useAlert } from 'dashboard/composables';
import AddTriggerWizard from './AddTriggerWizard.vue';

const props = defineProps({
  pipeline: { type: Object, required: true },
  accountId: { type: [String, Number], required: true },
});

const emit = defineEmits(['close']);

const bots = ref([]);
const allPipelines = ref([]);
const loading = ref(true);
const error = ref(null);

async function loadAllPipelines() {
  try {
    const { data } = await PipelinesAPI.get();
    const arr = data?.payload || data || [];
    // Cada pipeline trae solo metadata aquí. Para los stages hacemos un round-trip extra
    // por cada pipeline. Para optimizar, fetch en paralelo.
    const detailed = await Promise.all(
      arr.map(p => PipelinesAPI.show(p.id).then(r => r.data).catch(() => p))
    );
    allPipelines.value = detailed;
  } catch (_) {
    allPipelines.value = props.pipeline ? [props.pipeline] : [];
  }
}

// Wizard "Agregar disparador" abierto para una etapa específica
// editingPayload = { bot, trigger } cuando se está editando
const wizardOpenForStage = ref(null);
const editingPayload = ref(null);
function openWizard(stage) {
  editingPayload.value = null;
  wizardOpenForStage.value = stage;
}
function openWizardEdit(stage, bot, trigger) {
  // V4 · 2026-05-14 (Victor) · Multi-etapa: si el trigger pertenece a un grupo
  // enlazado, calculamos las stages del grupo para que el wizard precargue las
  // checkboxes correctamente (mostrar el alcance real, no solo la stage actual).
  let linkedStageIds = null;
  let linkedCount = null;
  if (trigger.linked_group_id) {
    const siblings = [];
    for (const b of bots.value) {
      for (const t of (b.triggers || [])) {
        if (t.linked_group_id === trigger.linked_group_id) {
          const sid = Number(t.config?.stage_id);
          if (sid > 0) siblings.push(sid);
        }
      }
    }
    linkedStageIds = Array.from(new Set(siblings));
    linkedCount = linkedStageIds.length;
  }
  editingPayload.value = { bot, trigger, linkedStageIds, linkedCount };
  wizardOpenForStage.value = stage;
}
function closeWizard() {
  wizardOpenForStage.value = null;
  editingPayload.value = null;
}
async function onTriggerCreated() {
  closeWizard();
  await loadBots();
}

// Delete con confirm
const deletingPayload = ref(null);
function askDelete(bot, trigger) {
  deletingPayload.value = { bot, trigger };
}
function cancelDelete() { deletingPayload.value = null; }
async function confirmDelete(deleteAllLinked = false) {
  const p = deletingPayload.value;
  if (!p) return;
  try {
    await BotsAPI.deleteTrigger(p.bot.id, p.trigger.id, { delete_all_linked: deleteAllLinked });
    useAlert(deleteAllLinked ? 'Disparadores del grupo eliminados' : 'Disparador eliminado');
    deletingPayload.value = null;
    await loadBots();
  } catch (e) {
    useAlert(`Error al eliminar: ${e.response?.data?.message || e.message}`);
  }
}

// V4 · helper para mostrar info del grupo enlazado en el confirm-delete
function deletingLinkedInfo() {
  const t = deletingPayload.value?.trigger;
  if (!t?.linked_group_id) return null;
  return { count: t.linked_count || 1 };
}

// V5 · 2026-05-14 (Victor) · Copy-paste sticky-cursor estilo Kommo:
// click 📋 en un trigger → queda "pegado" al cursor del mouse · click en otra
// stage (o la misma) → crea copia independiente (sin linked_group_id).
const copyingPayload = ref(null); // { bot, trigger }
const cursorPos = ref({ x: 0, y: 0 });
function startCopy(bot, trigger) {
  copyingPayload.value = { bot, trigger };
}
function cancelCopy() {
  copyingPayload.value = null;
}
async function pasteCopyInStage(stage) {
  if (!copyingPayload.value) return;
  const { bot, trigger } = copyingPayload.value;

  // V5.1 · 2026-05-14 (Victor) · Paste SMOOTH sin parpadeo:
  // 1. Insertamos el trigger optimista en el state local con flag _spawn (CSS animación)
  // 2. Cerramos el modo copy inmediatamente (cursor + banner se van)
  // 3. POST al backend EN BACKGROUND (no await visual)
  // 4. Si OK → reemplaza el optimista con la respuesta real (mismo nodo, sin re-render)
  // 5. Si falla → rollback (quita el optimista) + toast de error
  // → Resultado: feedback en <50ms, cero parpadeo, cero re-fetch global
  const cfg = JSON.parse(JSON.stringify(trigger.config || {}));
  cfg.stage_id = stage.id;
  delete cfg.stage_ids;
  cfg.conditions = [{ type: 'stage', values: [stage.id] }];

  const tmpId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const optimisticTrigger = {
    id: tmpId,
    bot_id: bot.id,
    trigger_type: trigger.trigger_type,
    active: trigger.active !== false,
    config: cfg,
    human_name: trigger.human_name,
    _spawn: true,    // gatilla animación CSS .spawn
    _pending: true,  // visualmente "sutil" hasta que el POST confirme
  };
  const targetBot = bots.value.find(b => b.id === bot.id);
  if (targetBot) {
    targetBot.triggers = [...(targetBot.triggers || []), optimisticTrigger];
  }
  copyingPayload.value = null; // cierra modo copy inmediato

  try {
    const { data } = await BotsAPI.createTrigger(bot.id, {
      trigger_type: trigger.trigger_type,
      active: trigger.active !== false,
      config: cfg,
    });
    // Reemplaza el optimista IN-PLACE para preservar el nodo DOM (sin re-render)
    if (targetBot && data?.id) {
      const idx = targetBot.triggers.findIndex(t => t.id === tmpId);
      if (idx >= 0) {
        targetBot.triggers.splice(idx, 1, { ...data, _spawn: true });
      }
    }
  } catch (e) {
    // Rollback: quita el optimista y avisa el error
    if (targetBot) {
      targetBot.triggers = targetBot.triggers.filter(t => t.id !== tmpId);
    }
    useAlert(`Error al copiar: ${e.response?.data?.message || e.message}`);
  }
}
function onMouseMove(e) {
  if (!copyingPayload.value) return;
  cursorPos.value = { x: e.clientX, y: e.clientY };
}
function onKeyDown(e) {
  if (e.key === 'Escape' && copyingPayload.value) {
    e.stopPropagation();
    cancelCopy();
  }
}
onMounted(() => {
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('keydown', onKeyDown);
});
onUnmounted(() => {
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('keydown', onKeyDown);
});

async function loadBots() {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await BotsAPI.get();
    bots.value = data.payload || data || [];
  } catch (e) {
    error.value = e.response?.status ? `HTTP ${e.response.status}` : e.message;
  } finally {
    loading.value = false;
  }
}

// Propuesta A (Victor 2026-05-15): pastilla Activo/Pausado en la tarjeta del
// disparador. Click alterna bot.active vía toggle_active · se refleja aquí, en
// la lista de Salesbots y en el resto de embudos (misma fuente: el bot).
const togglingBotId = ref(null);
async function toggleBotActive(bot, ev) {
  ev?.stopPropagation();
  if (togglingBotId.value) return;
  togglingBotId.value = bot.id;
  try {
    const { data } = await BotsAPI.toggleActive(bot.id);
    const newActive = data?.active ?? !bot.active;
    bots.value.forEach(b => {
      if (b.id === bot.id) b.active = newActive;
    });
  } finally {
    togglingBotId.value = null;
  }
}

onMounted(loadBots);
onMounted(loadAllPipelines);

// Lock body scroll mientras el modal está abierto
onMounted(() => {
  document.body.style.overflow = 'hidden';
});
function close() {
  document.body.style.overflow = '';
  emit('close');
}

// ── Mapear triggers por etapa (uno por trigger, no por bot) ──
// Un trigger pertenece a una etapa si su config.stage_id matchea
// (o si conditions[].type=stage incluye el id de la etapa).
function triggerStageIds(trigger) {
  const cfg = trigger.config || {};
  const ids = [];
  if (cfg.stage_id != null) ids.push(Number(cfg.stage_id));
  const conds = Array.isArray(cfg.conditions) ? cfg.conditions : [];
  for (const c of conds) {
    if (c && c.type === 'stage' && Array.isArray(c.values)) {
      for (const v of c.values) ids.push(Number(v));
    }
  }
  return Array.from(new Set(ids));
}

const triggersByStage = computed(() => {
  const out = {};
  for (const stage of props.pipeline?.stages || []) out[stage.id] = [];
  for (const bot of bots.value) {
    const triggers = Array.isArray(bot.triggers) ? bot.triggers : [];
    for (const trig of triggers) {
      const sids = triggerStageIds(trig);
      for (const sid of sids) {
        if (out[sid]) out[sid].push({ bot, trigger: trig });
      }
    }
  }
  return out;
});

const totalTriggers = computed(() =>
  Object.values(triggersByStage.value).reduce((acc, arr) => acc + arr.length, 0)
);

// Trigger summary mapping (UI-friendly text)
function triggerWhenLabel(trigger) {
  const cfg = trigger?.config || {};
  const delay = Number(cfg.delay_hours || 0);
  const base = (() => {
    if (trigger?.trigger_type === 'stage_created') return 'Cuando se crea en esta etapa';
    if (trigger?.trigger_type === 'stage_changed') return 'Cuando es movido a esta etapa';
    if (trigger?.trigger_type === 'stage_any') return 'Cuando se crea o es movido a esta etapa';
    if (trigger?.trigger_type === 'message_received') return 'Cuando recibe un mensaje';
    if (trigger?.trigger_type === 'tag_added') return 'Cuando agrega etiqueta';
    if (trigger?.trigger_type === 'manual') return 'Lanzamiento manual';
    return trigger?.trigger_type || 'Disparador';
  })();
  if (delay > 0) {
    const unit = delay >= 24 && delay % 24 === 0
      ? `${delay / 24} día${delay / 24 === 1 ? '' : 's'}`
      : `${delay} hora${delay === 1 ? '' : 's'}`;
    return `${base} después de ${unit}`;
  }
  return base;
}

function scheduleLabel(trigger) {
  const sch = trigger?.config?.schedule || {};
  if (sch.mode !== 'custom') return null;
  const dayMap = { Mo: 'L', Tu: 'M', We: 'X', Th: 'J', Fr: 'V', Sa: 'S', Su: 'D' };
  const days = Array.isArray(sch.days) ? sch.days.map(d => dayMap[d] || d).join('') : '';
  const from = sch.from || '';
  const to = sch.to || '';
  return `${days} · ${from} - ${to}`;
}

// Fase 3b: helpers para mostrar info de change_stage en card
function isChangeStage(trigger) {
  return (trigger?.config?.action_type || 'execute_bot') === 'change_stage';
}

function changeStageTargetLabel(trigger) {
  const cfg = trigger?.config || {};
  const targetStageId = Number(cfg.target_stage_id || 0);
  const targetPipelineId = Number(cfg.target_pipeline_id || 0);
  if (!targetStageId) return '—';
  const p = (allPipelines.value || []).find(pp => pp.id === targetPipelineId)
        || props.pipeline;
  const s = (p?.stages || []).find(ss => ss.id === targetStageId);
  return s?.name || `Etapa #${targetStageId}`;
}

function changeStageTargetPipelineName(trigger) {
  const cfg = trigger?.config || {};
  const targetPipelineId = Number(cfg.target_pipeline_id || 0);
  if (!targetPipelineId) return '';
  const p = (allPipelines.value || []).find(pp => pp.id === targetPipelineId);
  return p?.name || '';
}

function pipelineInitial(p) {
  return (p?.name || '?').trim().charAt(0).toUpperCase();
}

// Cerrar con Escape
function onKeydown(e) {
  if (e.key === 'Escape') close();
}
onMounted(() => window.addEventListener('keydown', onKeydown));
watch(() => null, () => {}, { onCleanup: () => window.removeEventListener('keydown', onKeydown) });
</script>

<template>
  <div class="d1-overlay" @click.self="close">
    <div class="d1-modal">

      <!-- Toolbar -->
      <div class="d1-toolbar">
        <button class="d1-toolbar-back" @click="close">
          <span>←</span> Atrás
        </button>
        <div class="d1-toolbar-title">
          <div class="d1-toolbar-title-icon">{{ pipelineInitial(pipeline) }}</div>
          <div>
            <div class="d1-toolbar-title-name">{{ pipeline?.name || 'Embudo' }} · Disparadores</div>
            <div class="d1-toolbar-title-tagline">
              {{ pipeline?.stages?.length || 0 }} etapas · {{ totalTriggers }} disparadores · Configura automatizaciones por etapa
            </div>
          </div>
        </div>
        <div class="d1-toolbar-spacer"></div>
        <button class="d1-toolbar-save d1-toolbar-save-disabled" title="Fase 2: edición CRUD próximamente" @click="comingSoon">
          💾 Guardar cambios
        </button>
      </div>

      <!-- Sidebar -->
      <div class="d1-sidebar">
        <div class="d1-section">
          <div class="d1-section-title">Fuentes de leads</div>
          <div style="font-size:11px;color:rgba(255,255,255,0.55);line-height:1.5;padding:8px 4px">
            Configura las fuentes (WhatsApp, Instagram, Facebook…) desde
            <a href="#" style="color:#fbbf24;text-decoration:underline">Ajustes → Bandejas de entrada</a>.
          </div>
        </div>

        <div class="d1-section">
          <div class="d1-section-title">Control duplicados</div>
          <div class="d1-sb-item">
            <div class="d1-sb-info">
              <div class="d1-sb-name">Detectar y unir</div>
              <div class="d1-sb-tag">Por teléfono · 24h ventana</div>
            </div>
            <div class="d1-sb-toggle" title="Próximamente"></div>
          </div>
        </div>

        <div class="d1-section">
          <div class="d1-section-title">Razones de pérdida</div>
          <div style="font-size:10.5px;color:rgba(255,255,255,0.55);line-height:1.5">
            Registra por qué se pierden leads (precio, competencia, ghosting…) y mejora tu funnel.
            <br><a href="#" style="color:#fbbf24;text-decoration:underline" @click.prevent="comingSoon">Configurar</a>
          </div>
        </div>
      </div>

      <!-- Main columns -->
      <div class="d1-main">
        <div v-if="loading" class="d1-loading">Cargando disparadores…</div>
        <div v-else-if="error" class="d1-error">Error al cargar: {{ error }}</div>
        <div v-else class="d1-stages-row" :class="{ 'd1-copying': !!copyingPayload }">
          <div
            v-for="stage in pipeline?.stages || []"
            :key="stage.id"
            class="d1-stage-col"
            :class="{ 'd1-stage-droptarget': !!copyingPayload }"
            @click="copyingPayload && pasteCopyInStage(stage)"
          >
            <div class="d1-stage-top-bar" :style="{ background: stage.color, boxShadow: `0 0 10px ${stage.color}` }"></div>
            <div class="d1-stage-head">
              <span class="d1-stage-handle">⋮⋮</span>
              <span class="d1-stage-name">{{ stage.name }}</span>
              <span class="d1-stage-handle">⋮</span>
            </div>
            <div class="d1-stage-tip">
              <a href="#" @click.prevent="comingSoon">+ Añadir consejos</a>
            </div>
            <div class="d1-stage-body">
              <div
                v-for="item in (triggersByStage[stage.id] || [])"
                :key="item.trigger.id"
                class="d1-trig"
                :class="{
                  'd1-trig-change-stage': isChangeStage(item.trigger),
                  'd1-trig-spawn': item.trigger._spawn,
                  'd1-trig-pending': item.trigger._pending,
                  'd1-trig-paused': !isChangeStage(item.trigger) && item.bot.active === false
                }"
                @click="openWizardEdit(stage, item.bot, item.trigger)"
              >
                <button
                  class="d1-trig-copy"
                  title="Copiar disparador · click luego en otra etapa para pegar (ESC cancela)"
                  @click.stop="startCopy(item.bot, item.trigger)"
                >📋</button>
                <button
                  class="d1-trig-delete"
                  title="Eliminar disparador"
                  @click.stop="askDelete(item.bot, item.trigger)"
                >🗑</button>
                <!-- V4 · Badge "enlazado" si el trigger es parte de un grupo multi-etapa -->
                <div
                  v-if="item.trigger.linked_group_id"
                  class="d1-trig-linked"
                  :title="`Enlazado a ${item.trigger.linked_count || '?'} etapas · editar uno te preguntará si propagar al grupo`"
                >⛓ {{ item.trigger.linked_count || '?' }}</div>
                <div v-if="scheduleLabel(item.trigger)" class="d1-trig-schedule">
                  {{ scheduleLabel(item.trigger) }}
                </div>
                <div class="d1-trig-row">
                  <template v-if="isChangeStage(item.trigger)">
                    <div class="d1-trig-icon stage">↻</div>
                    <div class="d1-trig-content">
                      <div class="d1-trig-when">{{ triggerWhenLabel(item.trigger) }}</div>
                      <div class="d1-trig-action">
                        Cambiar etapa principal:
                        <div class="d1-trig-pills">
                          <span v-if="changeStageTargetPipelineName(item.trigger)" class="d1-trig-pill">{{ changeStageTargetPipelineName(item.trigger) }}</span>
                          <span class="d1-trig-pill warn">{{ changeStageTargetLabel(item.trigger) }}</span>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="d1-trig-icon bot">🤖</div>
                    <div class="d1-trig-content">
                      <div class="d1-trig-when">{{ triggerWhenLabel(item.trigger) }}</div>
                      <div class="d1-trig-action">Ejecutar Salesbot:<br>{{ item.bot.name }}</div>
                      <button
                        type="button"
                        class="d1-trig-toggle"
                        :class="item.bot.active === false ? 'is-off' : 'is-on'"
                        :disabled="togglingBotId === item.bot.id"
                        :title="item.bot.active === false ? 'Bot pausado · click para reanudar' : 'Bot activo · click para pausar'"
                        @click.stop="toggleBotActive(item.bot, $event)"
                      >
                        {{ item.bot.active === false ? '⏸ Pausado' : '● Activo' }}
                      </button>
                    </div>
                  </template>
                </div>
              </div>
              <div v-if="(triggersByStage[stage.id] || []).length === 0" class="d1-stage-empty">
                Sin disparadores aún
              </div>
              <button class="d1-add-trig" @click="openWizard(stage)">
                <span class="d1-add-trig-icon">+</span>
                Agregar disparador
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Wizard: agregar/editar disparador (anidado sobre el modal) -->
    <AddTriggerWizard
      v-if="wizardOpenForStage"
      :stage="wizardOpenForStage"
      :pipeline="pipeline"
      :bots="bots"
      :all-pipelines="allPipelines"
      :editing="editingPayload"
      @close="closeWizard"
      @created="onTriggerCreated"
      @updated="onTriggerCreated"
    />

    <!-- V5 · Sticky cursor preview cuando hay copia activa -->
    <div
      v-if="copyingPayload"
      class="d1-copy-cursor"
      :style="{ left: cursorPos.x + 12 + 'px', top: cursorPos.y + 16 + 'px' }"
    >
      <span class="d1-copy-cursor-icon">📋</span>
      <span class="d1-copy-cursor-text">
        Copiando · <b>{{ copyingPayload.bot.name }}</b>
      </span>
    </div>
    <div v-if="copyingPayload" class="d1-copy-banner">
      📋 Modo copiar activo · click en cualquier etapa para pegar · ESC para cancelar
      <button class="d1-copy-cancel" @click.stop="cancelCopy">✕ Cancelar</button>
    </div>

    <!-- Confirm delete dialog -->
    <div v-if="deletingPayload" class="d1-confirm-overlay" @click.self="cancelDelete">
      <div class="d1-confirm">
        <div class="d1-confirm-icon">🗑</div>
        <div class="d1-confirm-title">¿Eliminar disparador?</div>
        <div class="d1-confirm-body">
          <template v-if="deletingLinkedInfo()">
            Este disparador está <b>enlazado a un grupo de {{ deletingLinkedInfo().count }} etapas</b>.
            <br><br>
            Elige una opción:
          </template>
          <template v-else>
            <b>{{ deletingPayload.bot.name }}</b> ya no se ejecutará automáticamente.
            Esta acción no se puede deshacer.
          </template>
        </div>
        <div class="d1-confirm-actions">
          <button class="d1-confirm-cancel" @click="cancelDelete">Cancelar</button>
          <button v-if="deletingLinkedInfo()" class="d1-confirm-delete" @click="confirmDelete(false)" style="background:#7c2d12;">Solo este</button>
          <button class="d1-confirm-delete" @click="confirmDelete(!!deletingLinkedInfo())">
            {{ deletingLinkedInfo() ? `Eliminar los ${deletingLinkedInfo().count}` : 'Eliminar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* === D1 · Disparadores · Kommo direct port (M1.B) === */
.d1-overlay {
  position: fixed; inset: 0;
  z-index: 9999;
  background: #050505;
  padding: 0;
  display: flex;
}
.d1-modal {
  background: #050505;
  border: 0;
  border-radius: 0;
  overflow: hidden;
  display: grid;
  grid-template-columns: 240px 1fr;
  grid-template-rows: 60px 1fr;
  width: 100vw;
  height: 100vh;
  max-width: none;
}

/* Top toolbar */
.d1-toolbar {
  grid-column: 1 / 3;
  display: flex; align-items: center; gap: 16px;
  padding: 0 20px;
  background: linear-gradient(180deg, rgba(20,20,20,0.95), rgba(8,8,8,0.95));
  border-bottom: 1px solid rgba(255,255,255,0.08);
  position: relative; overflow: hidden;
}
.d1-toolbar::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(120deg, transparent 38%, rgba(255,255,255,0.05) 50%, transparent 52%),
    radial-gradient(circle at 100% 0%, rgba(251,191,36,0.08), transparent 50%);
}
.d1-toolbar > * { position: relative; z-index: 1; }
.d1-toolbar-back {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 8px;
  color: rgba(255,255,255,0.75);
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.d1-toolbar-back:hover { background: rgba(255,255,255,0.08); color: #fff; }
.d1-toolbar-title {
  display: flex; align-items: center; gap: 10px;
}
.d1-toolbar-title-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; color: #1a1209; font-weight: 900;
  box-shadow: 0 2px 8px rgba(251,191,36,0.40);
}
.d1-toolbar-title-name {
  font-size: 14px; font-weight: 900; color: #fff;
}
.d1-toolbar-title-tagline {
  font-size: 10.5px; color: rgba(255,255,255,0.50);
  margin-top: 2px; letter-spacing: 0.3px;
}
.d1-toolbar-spacer { flex: 1; }
.d1-toolbar-save {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #1a1209;
  border: 0;
  border-radius: 9px;
  font-size: 12px; font-weight: 800;
  cursor: pointer;
  letter-spacing: 0.3px;
  box-shadow: 0 3px 12px rgba(251,191,36,0.40);
  font-family: inherit;
}
.d1-toolbar-save-disabled { opacity: 0.55; cursor: not-allowed; }

/* Left sidebar */
.d1-sidebar {
  background: linear-gradient(180deg, rgba(15,15,15,0.95), rgba(8,8,8,0.95));
  border-right: 1px solid rgba(255,255,255,0.05);
  padding: 18px 14px;
  overflow-y: auto;
  font-size: 12px;
}
.d1-section { margin-bottom: 22px; }
.d1-section-title {
  font-size: 9.5px; font-weight: 900;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase; letter-spacing: 1.4px;
  margin-bottom: 8px;
}
.d1-sb-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px;
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
  margin-bottom: 6px;
}
.d1-sb-info { flex: 1; min-width: 0; line-height: 1.2; }
.d1-sb-name { font-size: 11px; font-weight: 700; color: #fff; }
.d1-sb-tag { font-size: 9.5px; color: rgba(255,255,255,0.45); margin-top: 1px; }
.d1-sb-toggle {
  width: 26px; height: 14px;
  border-radius: 999px;
  background: rgba(251,191,36,0.30);
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
}
.d1-sb-toggle::after {
  content: '';
  position: absolute;
  top: 1px; right: 1px;
  width: 12px; height: 12px;
  background: #fbbf24;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(251,191,36,0.40);
}

/* Main area: columns of stages with triggers */
.d1-main {
  overflow-x: auto;
  overflow-y: auto;
  padding: 14px;
  background: #050505;
}
.d1-loading, .d1-error {
  padding: 40px 20px;
  text-align: center;
  font-size: 13px;
  color: rgba(255,255,255,0.55);
}
.d1-error { color: #f87171; }
.d1-stages-row {
  display: flex;
  gap: 8px;
  min-width: max-content;
  height: 100%;
}
.d1-stage-col {
  width: 290px;
  flex-shrink: 0;
  display: flex; flex-direction: column;
}
.d1-stage-top-bar {
  height: 3px;
  border-radius: 999px;
  margin-bottom: 4px;
}
.d1-stage-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px;
  background: rgba(20,15,5,0.7);
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 10px 10px 0 0;
  border-bottom: 0;
}
.d1-stage-name {
  font-size: 10.5px; font-weight: 900; color: #fff;
  letter-spacing: 1.2px; text-transform: uppercase;
  flex: 1; text-align: center;
}
.d1-stage-handle {
  font-size: 12px; color: rgba(255,255,255,0.40);
  cursor: grab;
  padding: 0 2px;
}
.d1-stage-tip {
  text-align: center;
  padding: 8px;
  font-size: 10.5px;
  color: rgba(255,255,255,0.50);
  background: rgba(255,255,255,0.02);
  border-left: 1px solid rgba(251,191,36,0.20);
  border-right: 1px solid rgba(251,191,36,0.20);
}
.d1-stage-tip a { color: #fbbf24; text-decoration: underline; cursor: pointer; }
.d1-stage-body {
  flex: 1;
  background: rgba(15,15,15,0.5);
  border: 1px solid rgba(251,191,36,0.20);
  border-top: 0;
  border-radius: 0 0 10px 10px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 420px;
}
.d1-stage-empty {
  text-align: center;
  padding: 14px;
  font-size: 10.5px;
  color: rgba(255,255,255,0.30);
  font-style: italic;
}

/* Trigger card */
.d1-trig {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  padding: 10px;
  position: relative;
  transition: all 0.15s;
  cursor: pointer;
}
/* Propuesta A · bot pausado: tarjeta atenuada + borde punteado */
.d1-trig-paused {
  opacity: 0.55;
  border-style: dashed;
}
.d1-trig-paused:hover { opacity: 0.8; }
/* Pastilla toggle Activo / Pausado */
.d1-trig-toggle {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 800;
  border-radius: 999px;
  padding: 3px 10px;
  cursor: pointer;
  font-family: inherit;
  border: 1px solid;
  transition: filter 0.15s;
}
.d1-trig-toggle:hover:not(:disabled) { filter: brightness(1.25); }
.d1-trig-toggle:disabled { opacity: 0.6; cursor: wait; }
.d1-trig-toggle.is-on {
  color: #5fbf86;
  background: rgba(60,180,110,0.14);
  border-color: rgba(60,180,110,0.40);
}
.d1-trig-toggle.is-off {
  color: #d8a23e;
  background: rgba(216,162,62,0.16);
  border-color: rgba(216,162,62,0.45);
}

.d1-trig:hover {
  border-color: rgba(251,191,36,0.40);
  background: rgba(255,255,255,0.05);
  transform: translateY(-1px);
}
.d1-trig-delete {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px; height: 22px;
  border: 0;
  background: rgba(0,0,0,0.50);
  color: rgba(255,255,255,0.55);
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
  font-family: inherit;
}
.d1-trig:hover .d1-trig-delete { opacity: 1; }
.d1-trig-delete:hover {
  background: rgba(239,68,68,0.20);
  color: #f87171;
}
/* V4 · Badge "enlazado" cuando el trigger es parte de un grupo multi-etapa */
.d1-trig-linked {
  position: absolute;
  top: 4px;
  right: 60px;
  font-size: 9.5px;
  font-weight: 800;
  color: #c4b5fd;
  background: rgba(139,92,246,0.18);
  border: 1px solid rgba(139,92,246,0.40);
  border-radius: 6px;
  padding: 2px 7px;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
/* V5 · Botón copiar (al lado del delete, mismo estilo, color dorado) */
.d1-trig-copy {
  position: absolute;
  top: 4px;
  right: 30px;
  width: 22px; height: 22px;
  border: 0;
  background: rgba(0,0,0,0.50);
  color: rgba(255,255,255,0.55);
  border-radius: 6px;
  font-size: 11px;
  cursor: copy;
  opacity: 0;
  transition: all 0.15s;
  font-family: inherit;
}
.d1-trig:hover .d1-trig-copy { opacity: 1; }
.d1-trig-copy:hover {
  background: rgba(251,191,36,0.20);
  color: #fbbf24;
}
/* V5 · Cursor flotante en modo copy */
.d1-copy-cursor {
  position: fixed;
  z-index: 10001;
  pointer-events: none;
  padding: 8px 12px;
  border-radius: 9px;
  background: linear-gradient(135deg, #1f2937, #0f172a);
  border: 2px solid #fbbf24;
  box-shadow: 0 8px 32px rgba(251,191,36,0.40), 0 4px 16px rgba(0,0,0,0.50);
  font-size: 11px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  animation: d1-copy-bob 2s ease-in-out infinite;
}
@keyframes d1-copy-bob {
  0%,100% { transform: translateY(0) rotate(-1.5deg); }
  50% { transform: translateY(-3px) rotate(0deg); }
}
.d1-copy-cursor-icon { font-size: 14px; }
.d1-copy-cursor-text { color: rgba(255,255,255,0.92); }
.d1-copy-cursor-text b { color: #fbbf24; }
/* V5 · Banner superior modo copy */
.d1-copy-banner {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  padding: 10px 18px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(251,191,36,0.95), rgba(245,158,11,0.95));
  color: #000;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 4px 24px rgba(251,191,36,0.50);
  display: flex;
  align-items: center;
  gap: 14px;
}
.d1-copy-cancel {
  background: rgba(0,0,0,0.20);
  color: #000;
  border: 1px solid rgba(0,0,0,0.30);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}
.d1-copy-cancel:hover { background: rgba(0,0,0,0.35); }
/* V5.1 · Trigger recién pegado · animación spawn smooth (sin parpadeo) */
.d1-trig.d1-trig-spawn {
  animation: d1-trig-spawn 0.42s cubic-bezier(.34, 1.56, .64, 1) both;
}
@keyframes d1-trig-spawn {
  0%   { opacity: 0; transform: scale(0.85) translateY(-8px); }
  60%  { opacity: 1; transform: scale(1.03) translateY(0); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}
/* Mientras el POST confirma, mostramos el trigger un pelín atenuado */
.d1-trig.d1-trig-pending {
  opacity: 0.85;
  pointer-events: none; /* no editable hasta que vuelva el id real del backend */
}
.d1-trig.d1-trig-pending::after {
  content: "guardando…";
  position: absolute;
  bottom: 4px;
  right: 8px;
  font-size: 8.5px;
  color: rgba(251,191,36,0.85);
  font-style: italic;
  letter-spacing: 0.3px;
}
/* V5 · Columnas en modo copy (drop target) */
.d1-stages-row.d1-copying .d1-stage-col {
  cursor: copy;
  transition: all 0.2s ease;
}
.d1-stages-row.d1-copying .d1-stage-col.d1-stage-droptarget {
  border: 1.5px dashed rgba(251,191,36,0.45);
  box-shadow: inset 0 0 0 1px rgba(251,191,36,0.10);
}
.d1-stages-row.d1-copying .d1-stage-col.d1-stage-droptarget:hover {
  background: linear-gradient(180deg, rgba(251,191,36,0.08), rgba(20,20,20,0.55));
  border-color: #fbbf24;
  box-shadow: 0 0 24px rgba(251,191,36,0.30), inset 0 0 0 1px rgba(251,191,36,0.30);
  transform: translateY(-2px);
}
.d1-trig-schedule {
  display: inline-block;
  font-size: 9.5px;
  color: rgba(255,255,255,0.65);
  background: rgba(96,165,250,0.10);
  border: 1px solid rgba(96,165,250,0.25);
  border-radius: 6px;
  padding: 2px 7px;
  margin-bottom: 6px;
  font-weight: 700;
  letter-spacing: 0.4px;
}
.d1-trig-row {
  display: flex; align-items: flex-start; gap: 8px;
}
.d1-trig-icon {
  width: 24px; height: 24px;
  border-radius: 6px;
  background: rgba(96,165,250,0.20);
  color: #60a5fa;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}
.d1-trig-icon.stage {
  background: rgba(192,132,252,0.20);
  color: #c084fc;
}
.d1-trig-change-stage {
  border-left: 3px solid #c084fc;
  padding-left: 8px;
}
.d1-trig-change-stage:hover {
  border-color: rgba(192,132,252,0.50);
  border-left-color: #c084fc;
}
.d1-trig-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.d1-trig-pill {
  font-size: 9.5px;
  padding: 2px 8px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 999px;
  color: rgba(255,255,255,0.80);
  font-weight: 700;
}
.d1-trig-pill.warn {
  background: rgba(239,68,68,0.10);
  border-color: rgba(239,68,68,0.30);
  color: #f87171;
}
.d1-trig-content { flex: 1; min-width: 0; }
.d1-trig-when {
  font-size: 10.5px; color: rgba(255,255,255,0.65);
  line-height: 1.4;
}
.d1-trig-action {
  font-size: 11.5px; font-weight: 800; color: #fff;
  margin-top: 4px;
  line-height: 1.35;
}

/* Add trigger button */
.d1-add-trig {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  width: 100%;
  padding: 10px;
  background: transparent;
  border: 1px dashed rgba(255,255,255,0.15);
  border-radius: 8px;
  color: rgba(255,255,255,0.45);
  font-size: 11px; font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  margin-top: auto;
  transition: all 0.15s;
}
.d1-add-trig:hover {
  border-color: rgba(251,191,36,0.50);
  color: #fbbf24;
  background: rgba(251,191,36,0.05);
}
.d1-add-trig-icon {
  width: 16px; height: 16px;
  border-radius: 50%;
  background: rgba(255,255,255,0.06);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 14px;
  font-weight: 900;
}
.d1-add-trig:hover .d1-add-trig-icon {
  background: rgba(251,191,36,0.20);
  color: #fbbf24;
}

/* Confirm delete dialog */
.d1-confirm-overlay {
  position: fixed; inset: 0;
  z-index: 110;
  background: rgba(0,0,0,0.70);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.d1-confirm {
  width: 100%;
  max-width: 420px;
  background: #0a0a0a;
  border: 1px solid rgba(239,68,68,0.30);
  border-radius: 14px;
  padding: 22px 24px;
  box-shadow: 0 24px 70px rgba(0,0,0,0.7), 0 0 30px rgba(239,68,68,0.10);
  text-align: center;
  position: relative;
  overflow: hidden;
}
.d1-confirm::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(120deg, transparent 38%, rgba(255,255,255,0.04) 50%, transparent 52%),
    radial-gradient(circle at 50% 0%, rgba(239,68,68,0.12), transparent 60%);
}
.d1-confirm > * { position: relative; z-index: 1; }
.d1-confirm-icon {
  width: 52px; height: 52px;
  margin: 0 auto 12px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(239,68,68,0.25), rgba(185,28,28,0.10));
  border: 1px solid rgba(239,68,68,0.40);
  color: #f87171;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 22px;
  box-shadow: 0 0 16px rgba(239,68,68,0.30);
}
.d1-confirm-title {
  font-size: 16px; font-weight: 900;
  color: #fff;
  margin-bottom: 8px;
}
.d1-confirm-body {
  font-size: 12.5px;
  line-height: 1.55;
  color: rgba(255,255,255,0.70);
  margin-bottom: 18px;
}
.d1-confirm-body b { color: #fbbf24; font-weight: 800; }
.d1-confirm-actions {
  display: flex; gap: 8px; justify-content: center;
}
.d1-confirm-cancel,
.d1-confirm-delete {
  padding: 9px 22px;
  border-radius: 9px;
  font-size: 12px; font-weight: 800;
  cursor: pointer;
  border: 0;
  font-family: inherit;
  letter-spacing: 0.3px;
}
.d1-confirm-cancel {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.80);
}
.d1-confirm-cancel:hover { background: rgba(255,255,255,0.10); color: #fff; }
.d1-confirm-delete {
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  color: #fff;
  box-shadow: 0 3px 12px rgba(239,68,68,0.40);
}
.d1-confirm-delete:hover { box-shadow: 0 5px 18px rgba(239,68,68,0.55); }
</style>
